import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { hasMenu } from 'auth/access';
import { useAuth } from 'modules/auth/auth.context';
import {
  ApproveRejectActions,
  ExpandableTable,
  InlineRejectionForm,
  SectionTitle,
  SegmentedControl,
} from 'modules/shared/components';
import { capitalizeString, formatAmount } from 'modules/shared/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import {
  CLOSED_STATUS_OPTIONS,
  PRIMARY_VIEW_OPTIONS,
  VOUCHER_ACTION_MODES,
  VOUCHER_CLOSED_STATUSES,
  VOUCHER_PRIMARY_VIEWS,
  VOUCHER_STATUS,
  VOUCHER_STATUS_KEYS,
} from '../constants';
import { useAdminVouchers } from '../hooks/useAdminVouchers';
import { approveVoucher, rejectVoucher } from '../service';
import { getEmptyMessage, getVisibleVouchers } from '../utils';

/**
 * @param {'active' | 'closed'} view
 * @returns {Array<{id: string, label: string, dataKey: string, renderCell: (value: any) => JSX.Element}>}
 */
function getAdminVouchersColumns({ view }) {
  const columns = [
    {
      id: 'date',
      label: 'Date',
      dataKey: 'dateOfEvent',
      sortable: true,
    },
    {
      id: 'name',
      label: 'Name',
      dataKey: 'employeeName',
      sortable: true,
    },
    {
      id: 'department',
      label: 'Department',
      dataKey: 'department',
      sortable: true,
      renderCell: capitalizeString,
    },
    {
      id: 'expenseType',
      label: 'Request type',
      dataKey: 'expenseType',
      sortable: true,
    },
    {
      id: 'amount',
      label: 'Amount',
      dataKey: 'amountRequested',
      sortable: true,
      renderCell: formatAmount,
    },
  ];

  if (view === VOUCHER_PRIMARY_VIEWS.CLOSED) {
    (columns as any[]).push({
      id: 'status',
      label: 'Status',
      dataKey: 'status',
      sortable: false,
      renderCell: (val: any) => {
        const config = VOUCHER_STATUS[val];

        if (!config) {
          return <span>-</span>;
        }
        return (
          <Typography sx={{ color: `${config.color}.main`, fontWeight: 500 }}>
            {config.label ?? '-'}
          </Typography>
        );
      },
    });
  }

  return columns;
}

export default function AdminVouchers() {
  const { user } = useAuth();
  const actorEmployeeId = user?.employee_id;

  const { vouchersByStatus, loading, refresh } = useAdminVouchers();

  const [view, setView] = useState(VOUCHER_PRIMARY_VIEWS.ACTIVE);
  const [closedStatus, setClosedStatus] = useState(VOUCHER_CLOSED_STATUSES.APPROVED);
  const [expandedRowId, setExpandedRowId] = useState(null);

  const [actionMode, setActionMode] = useState({
    type: VOUCHER_ACTION_MODES.IDLE,
    voucherId: null,
  });
  const [updating, setUpdating] = useState(false);

  const rejectionForm = useForm({ defaultValues: { rejectionReason: '' } });
  const { watch, trigger, resetField } = rejectionForm;
  const rejectionReason = watch('rejectionReason');

  const visibleVouchers = useMemo(() => {
    return getVisibleVouchers(vouchersByStatus, view, closedStatus);
  }, [vouchersByStatus, view, closedStatus]);

  const emptyMessage = getEmptyMessage(view, closedStatus);

  const columns = useMemo(() => getAdminVouchersColumns({ view }), [view]);

  const handleApprove = async (voucher) => {
    const voucherId = voucher?.id;
    const amountReimbursed = voucher?.amountRequested;
    if (!voucherId || amountReimbursed == null || !actorEmployeeId || updating) {
      return;
    }

    setUpdating(true);
    try {
      await approveVoucher(voucherId, amountReimbursed, actorEmployeeId);
      toast.success('Voucher request approved');
      setActionMode({ type: VOUCHER_ACTION_MODES.IDLE, voucherId: null });
      await refresh();
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to approve voucher request',
          error: err,
          code: 'VOUCHER_APPROVE_FAILED',
        }),
      );
    } finally {
      setUpdating(false);
    }
  };

  const startReject = (voucherId) => {
    if (updating) return;
    setActionMode({ type: VOUCHER_ACTION_MODES.REJECT, voucherId });
  };

  const resetReject = useCallback(() => {
    setActionMode({ type: VOUCHER_ACTION_MODES.IDLE, voucherId: null });
    resetField('rejectionReason');
  }, [resetField]);

  const handleReject = async (voucherId) => {
    const isValid = await trigger('rejectionReason');
    if (!isValid || !actorEmployeeId || updating) return;

    const reason = rejectionReason?.trim();
    if (!reason) return;

    setUpdating(true);
    try {
      await rejectVoucher(voucherId, reason, actorEmployeeId);
      toast.success('Voucher request rejected');
      resetReject();
      await refresh();
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to reject voucher request',
          error: err,
          code: 'VOUCHER_REJECT_FAILED',
        }),
      );
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    resetReject();
  }, [expandedRowId, view, closedStatus, resetReject]);

  return (
    <div className='column gap-4'>
      <SectionTitle title='Vouchers & Office Supplies Requests' />
      <div className='column gap-3'>
        <SegmentedControl
          options={PRIMARY_VIEW_OPTIONS}
          value={view}
          onChange={(v: string) => setView(v)}
          ariaLabel='Voucher view'
        />

        {view === VOUCHER_PRIMARY_VIEWS.CLOSED && (
          <SegmentedControl
            options={CLOSED_STATUS_OPTIONS}
            value={closedStatus}
            onChange={(v: string) => setClosedStatus(v)}
            ariaLabel='Closed voucher status'
          />
        )}
      </div>

      <ExpandableTable
        columns={columns}
        data={visibleVouchers}
        loading={loading}
        emptyMessage={emptyMessage}
        onExpandedRowChange={setExpandedRowId}
        renderExpandedRow={(voucher) => (
          <AdminVouchersExpandedRow
            voucherData={voucher}
            updating={updating}
            actionMode={actionMode}
            onApprove={handleApprove}
            onSubmitReject={handleReject}
            onStartReject={startReject}
            onCancelReject={resetReject}
            rejectionForm={rejectionForm}
          />
        )}
      />
    </div>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.voucherData
 * @param {boolean} props.updating
 * @param {Object} props.actionMode
 * @param {Function} props.onApprove
 * @param {Function} props.onSubmitReject
 * @param {Function} props.onStartReject
 * @param {Function} props.onCancelReject
 * @param {Object} props.rejectionForm
 * @returns {JSX.Element}
 */
function AdminVouchersExpandedRow({
  voucherData,
  updating,
  actionMode,
  onApprove,
  onSubmitReject,
  onStartReject,
  onCancelReject,
  rejectionForm,
}) {
  return (
    <div className='column'>
      <div className='row row--between gap-5'>
        <div>
          <span>Applied on: </span>
          <span className='font-medium'>{voucherData.appliedOn}</span>
        </div>
        <div className='row gap-3'>
          <span className='font-medium'>Note</span>
          <p>{voucherData.employeeNote || '—'}</p>
        </div>
      </div>

      {voucherData.status === VOUCHER_STATUS_KEYS.REJECTED && (
        <div className='row gap-3'>
          <span className='font-medium'>Reason for Rejection</span>
          <p>{voucherData.rejectionReason || '—'}</p>
        </div>
      )}

      {voucherData.attachmentUrl && (
        <div className='row gap-3'>
          <span className='font-medium'>Bill / attachment</span>
          <MuiLink href={voucherData.attachmentUrl} target='_blank' rel='noopener noreferrer'>
            Open attachment
          </MuiLink>
        </div>
      )}

      {/* Approving is the APPROVE action of Reimbursement; without it the queue is read-only. */}
      {voucherData.status === VOUCHER_STATUS_KEYS.PENDING && hasMenu('billing.reimbursement-approvals', 'APPROVE') && (
        <>
          {actionMode.type === VOUCHER_ACTION_MODES.IDLE && (
            <ApproveRejectActions
              loading={updating}
              onApprove={() => onApprove(voucherData)}
              onReject={() => onStartReject(voucherData.id)}
            />
          )}

          {actionMode.type === VOUCHER_ACTION_MODES.REJECT &&
            actionMode.voucherId === voucherData.id && (
              <InlineRejectionForm
                methods={rejectionForm}
                disabled={updating}
                onCancel={onCancelReject}
                onSubmit={() => onSubmitReject(voucherData.id)}
              />
            )}
        </>
      )}
    </div>
  );
}
